'use client';

import { Badge } from '@/components/ui/Badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { useAuth } from '@/context/AuthContext';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { motion, useScroll, useTransform } from 'motion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import * as React from 'react';
// import Banner from '../Banners/Banner';
import ImpersonationBanner from '../Banners/ImpersonationBanner';
import RewardBanner from '../Banners/RewardBanner';
import UnriddleToAnaraBanner from '../Banners/UnriddleToAnaraBanner';
import { Button } from '../Button/Button';
import Logo from '../Logo';
import {
  ListItem,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/NavigationMenu';
import { Separator } from '../ui/Separator';
const LoggedInIndicator = ({ email }: { email: string }) => {
  return (
    <div className="text-sm text-textGray flex flex-row items-center gap-2 bg-elementGray2 py-1.5 px-2 rounded-md">
      <div className="bg-blue w-3 h-3 rounded-full" />
      <span>{email}</span>
    </div>
  );
};

export default function LandingNavbar() {
  const { user } = useAuth();
  const router = useRouter();
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentView, setCurrentView] = React.useState<'main' | 'resources'>(
    'main',
  );
  const query = useSearchParams();

  const resourceItems = [
    // { label: 'Blog', href: '/blog' },
    { label: 'User guide', href: '/guide', target: '_blank' },
    { label: 'Changelog', href: '/changelog' },
    { label: 'Blog', href: '/blog' },
    { label: 'FAQs', href: '/faqs' },
  ];
  const { track } = useAnalytics();

  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 100], [0, 1]);

  const handleLoginClick = () => {
    router.push('/login');
    track('login_button_clicked', { location: 'navbar' });
  };
  const handleSignupClick = () => {
    router.push('/login');
    track('signup_button_clicked', { location: 'navbar' });
  };

  const handleBackToMenu = () => {
    setCurrentView('main');
  };

  const handleButtonClick = (href: string, label: string) => {
    router.push(href);
    track('navbar_button_clicked', { label });
  };
  return (
    <header className="fixed top-0 z-50 w-full bg-white/90 backdrop-blur-xl">
      <ImpersonationBanner />
      <RewardBanner />
      {/* <Banner /> */}
      <UnriddleToAnaraBanner />
      <div className="flex flex-row md:h-[88px] h-16 items-center justify-between px-6 mx-auto max-w-[1072px]">
        <Logo
          href={user ? '/homepage' : `/${query ? `?${query.toString()}` : ''}`}
          className="md:hidden "
        />
        <nav className="hidden md:flex items-center space-x-[6px]">
          <Logo
            href={
              user ? '/homepage' : `/${query ? `?${query.toString()}` : ''}`
            }
            className="pr-6 pt-1"
          />
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="font-semibold">
                  Resources
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  {resourceItems.map((item) => (
                    <ListItem
                      key={item.href}
                      className="w-[152px]"
                      onClick={() => {
                        if (item.target === '_blank') {
                          window.open(item.href, '_blank');
                        } else {
                          handleButtonClick(item.href, item.label);
                        }
                      }}
                    >
                      {item.label}
                    </ListItem>
                  ))}
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              window.open('https://tally.so/r/wbO0j0', '_blank');
              track('navbar_button_clicked', { label: 'sales' });
            }}
          >
            Contact sales
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              const queryString = query?.toString()
                ? `?${query.toString()}`
                : '';
              router.push(`/pricing${queryString}`);
              track('navbar_button_clicked', { label: 'pricing' });
            }}
          >
            Pricing
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              window.open('https://jobs.ashbyhq.com/anara', '_blank');
              track('navbar_button_clicked', { label: 'careers' });
            }}
          >
            Careers
            <Badge className="-ml-0.5" variant="new">
              We're hiring!
            </Badge>
            {/* <div className="-ml-1 bg-muted text-muted-foreground2 rounded-full w-6 h-6 flex items-center justify-center font-bold text-sm">
                2
              </div> */}
          </Button>
        </nav>

        <div className="hidden md:flex items-center">
          {user ? (
            <div className="flex flex-row gap-2.5">
              <LoggedInIndicator email={user.email} />
              <Button
                size="xs"
                onClick={() => {
                  router.push('/new');

                  track('navbar_button_clicked', {
                    label: 'logged_in_user_launch_app',
                  });
                }}
                className="bg-black text-white hover:bg-black/90"
              >
                Open app
              </Button>
            </div>
          ) : (
            <div className="flex flex-row gap-2.5">
              <Button size="xs" variant="secondary" onClick={handleLoginClick}>
                Log in
              </Button>
              <Button
                size="xs"
                onClick={handleSignupClick}
                className="bg-black text-white hover:bg-black/90"
              >
                Get Anara free
              </Button>
            </div>
          )}
        </div>

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon">
              {isOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="md:hidden w-[90vw] mx-[5vw] left-0">
            {currentView === 'main' ? (
              <div className="flex flex-col">
                {user ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      router.push('/new');

                      track('navbar_button_clicked', {
                        label: 'logged_in_user_launch_app',
                      });
                    }}
                    size="xs"
                    className="justify-start px-2 font-base text-sm"
                  >
                    Open app
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={handleLoginClick}
                    size="xs"
                    className="justify-start px-2 font-base text-sm"
                  >
                    Log in
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="xs"
                  className="justify-between px-2 font-base text-sm"
                  onClick={() => setCurrentView('resources')}
                >
                  Resources
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() =>
                    window.open('https://tally.so/r/wbO0j0', '_blank')
                  }
                  size="xs"
                  className="justify-start px-2 font-base text-sm"
                >
                  Contact sales
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const queryString = query?.toString()
                      ? `?${query.toString()}`
                      : '';
                    router.push(`/pricing${queryString}`);
                  }}
                  size="xs"
                  className="justify-start px-2 font-base text-sm"
                >
                  Pricing
                </Button>
                <Button
                  variant="ghost"
                  onClick={() =>
                    window.open('https://jobs.ashbyhq.com/anara', '_blank')
                  }
                  size="xs"
                  className="justify-start px-2 font-base text-sm"
                >
                  Careers
                </Button>
              </div>
            ) : (
              <div className="flex flex-col">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleBackToMenu}
                  className="justify-start text-sm text-textGray4 hover:text-textGray5"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to menu
                </Button>
                {resourceItems.map((item) => (
                  <Button
                    key={item.href}
                    variant="ghost"
                    size="xs"
                    className="justify-start px-2 font-base text-sm"
                    onClick={() => handleButtonClick(item.href, item.label)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
      <motion.div
        className="flex flex-row items-center justify-between px-6 mx-auto max-w-[1072px]"
        style={{ opacity }}
      >
        <Separator className="h-[1px] bg-border2" />
      </motion.div>
    </header>
  );
}
